// =============================================================================
// NOTIFICATION ITEM COMPONENT
// =============================================================================

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Notification, NotificationType } from '../../stores/notificationStore.js';

// =============================================================================
// TYPES
// =============================================================================

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case 'message':
      return '💬';
    case 'call':
      return '📞';
    case 'family':
      return '👨‍👩‍👧‍👦';
    case 'photo':
      return '📷';
    case 'system':
      return '🔔';
    default:
      return '🔔';
  }
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

// =============================================================================
// COMPONENT
// =============================================================================

export function NotificationItem({ notification, onRead, onDelete }: NotificationItemProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleClick = () => {
    // Mark as read if not already
    if (!notification.isRead) {
      onRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.data) {
      const data = notification.data as unknown as Record<string, unknown>;
      switch (notification.type) {
        case 'message':
          if (data.conversationId) {
            void navigate(`/chats/${data.conversationId}`);
          }
          break;
        case 'call':
          void navigate('/calls');
          break;
        case 'family':
          void navigate('/family');
          break;
        case 'photo':
          void navigate('/photos');
          break;
        default:
          break;
      }
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  return (
    <div
      className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${
        notification.isRead
          ? 'bg-surface-2 hover:bg-surface-3'
          : 'bg-surface-3 hover:bg-surface-sunken'
      }`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleClick();
      }}
    >
      {/* Unread indicator */}
      {!notification.isRead && (
        <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
      )}

      {/* Icon */}
      <div className="text-2xl flex-shrink-0">{getNotificationIcon(notification.type)}</div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium truncate ${notification.isRead ? "text-ink-2" : "text-ink-1"}`}>
            {notification.title}
          </p>
          <span className="text-xs text-ink-3 flex-shrink-0">
            {formatRelativeTime(notification.createdAt)}
          </span>
        </div>
        <p className={`text-sm mt-0.5 line-clamp-2 ${notification.isRead ? 'text-ink-3' : 'text-ink-3'}`}>
          {notification.body}
        </p>
      </div>

      {/* Delete button */}
      <button
        className="p-1 text-ink-3 hover:text-danger transition-colors flex-shrink-0"
        onClick={handleDelete}
        title={t('notifications.delete')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
}
